/**
 * Safe Discord-flavoured markdown: **bold**, *italic*, __underline__, ~~strike~~,
 * `code`, ```blocks```, > quotes, ||spoilers||, links and mentions. Produces React
 * elements only — there is no HTML injection path.
 */
import { useState, type ReactNode } from 'react';
import { customEmojiUrl } from '@comms/shared';
import { displayName, useStore } from '../store';

type Inline = { re: RegExp; render: (m: RegExpExecArray, key: string) => ReactNode };

function Spoiler({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState(false);
  return (
    <span
      className={shown ? 'spoiler revealed' : 'spoiler'}
      onClick={() => setShown(true)}
      role="button"
      tabIndex={0}
    >
      {children}
    </span>
  );
}

function UserMention({ id, guildId }: { id: string; guildId: string | null }) {
  const name = useStore((s) => displayName(s, id, guildId));
  const isMe = useStore((s) => s.me?.id === id);
  return (
    <span
      className={
        isMe
          ? 'rounded bg-warning/30 px-0.5 font-medium text-text-0'
          : 'rounded bg-accent/30 px-0.5 font-medium text-text-0'
      }
    >
      @{name}
    </span>
  );
}

function RoleMention({ id, guildId }: { id: string; guildId: string | null }) {
  const role = useStore((s) =>
    guildId ? s.guilds[guildId]?.roles.find((r) => r.id === id) : undefined,
  );
  const color = role?.color ? `#${role.color.toString(16).padStart(6, '0')}` : undefined;
  return (
    <span className="rounded bg-accent/30 px-0.5 font-medium" style={color ? { color } : undefined}>
      @{role?.name ?? 'deleted-role'}
    </span>
  );
}

function ChannelMention({ id }: { id: string }) {
  const name = useStore((s) => s.channels[id]?.name);
  return (
    <span className="rounded bg-accent/30 px-0.5 font-medium text-text-0">
      #{name ?? 'unknown'}
    </span>
  );
}

function safeHref(url: string): string | null {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
}

function inlineRules(guildId: string | null): Inline[] {
  return [
    {
      re: /<(a?):([A-Za-z0-9_]{2,32}):(\d{1,20})>/,
      render: (m, k) => (
        <img
          key={k}
          src={customEmojiUrl(m[3]!)}
          alt={`:${m[2]}:`}
          title={`:${m[2]}:`}
          className="inline-block h-[1.375em] w-[1.375em] object-contain align-bottom"
        />
      ),
    },
    {
      re: /`([^`\n]+)`/,
      render: (m, k) => (
        <code key={k} className="rounded bg-bg-0 px-1 py-0.5 text-[0.85em]">
          {m[1]}
        </code>
      ),
    },
    {
      re: /\|\|([\s\S]+?)\|\|/,
      render: (m, k) => <Spoiler key={k}>{parseInline(m[1]!, guildId, k)}</Spoiler>,
    },
    {
      re: /\*\*([\s\S]+?)\*\*/,
      render: (m, k) => <strong key={k}>{parseInline(m[1]!, guildId, k)}</strong>,
    },
    { re: /__([\s\S]+?)__/, render: (m, k) => <u key={k}>{parseInline(m[1]!, guildId, k)}</u> },
    { re: /~~([\s\S]+?)~~/, render: (m, k) => <s key={k}>{parseInline(m[1]!, guildId, k)}</s> },
    {
      re: /(?:\*([^*\n]+)\*|\b_([^_\n]+)_\b)/,
      render: (m, k) => <em key={k}>{parseInline(m[1] ?? m[2]!, guildId, k)}</em>,
    },
    {
      re: /<@!?(\d{1,20})>/,
      render: (m, k) => <UserMention key={k} id={m[1]!} guildId={guildId} />,
    },
    {
      re: /<@&(\d{1,20})>/,
      render: (m, k) => <RoleMention key={k} id={m[1]!} guildId={guildId} />,
    },
    { re: /<#(\d{1,20})>/, render: (m, k) => <ChannelMention key={k} id={m[1]!} /> },
    {
      re: /@(everyone|here)\b/,
      render: (m, k) => (
        <span key={k} className="rounded bg-accent/30 px-0.5 font-medium text-text-0">
          @{m[1]}
        </span>
      ),
    },
    {
      re: /<?(https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]'"])>?/,
      render: (m, k) => {
        const href = safeHref(m[1]!);
        return href ? (
          <a
            key={k}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-[#00a8fc] hover:underline"
          >
            {m[1]}
          </a>
        ) : (
          m[0]
        );
      },
    },
  ];
}

function parseInline(text: string, guildId: string | null, keyPrefix = 'i'): ReactNode[] {
  const rules = inlineRules(guildId);
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest.length > 0) {
    let best: { idx: number; m: RegExpExecArray; rule: Inline } | null = null;
    for (const rule of rules) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.idx)) best = { idx: m.index, m, rule };
    }
    if (!best) {
      out.push(rest);
      break;
    }
    if (best.idx > 0) out.push(rest.slice(0, best.idx));
    out.push(best.rule.render(best.m, `${keyPrefix}-${n++}`));
    rest = rest.slice(best.idx + best.m[0].length);
  }
  return out;
}

export function Markdown({
  content,
  guildId = null,
}: {
  content: string;
  guildId?: string | null;
}) {
  const blocks: ReactNode[] = [];
  const parts = content.split(/(```[\s\S]*?```)/g);
  parts.forEach((part, i) => {
    if (part.startsWith('```') && part.endsWith('```') && part.length >= 6) {
      const body = part.slice(3, -3).replace(/^[a-z0-9+-]*\n/i, '');
      blocks.push(
        <pre
          key={i}
          className="my-1 overflow-x-auto rounded border border-bg-0 bg-bg-1 p-2 text-sm"
        >
          <code>{body}</code>
        </pre>,
      );
      return;
    }
    const lines = part.split('\n');
    lines.forEach((line, j) => {
      const key = `${i}-${j}`;
      if (/^>\s?/.test(line)) {
        blocks.push(
          <div key={key} className="border-l-4 border-text-3 pl-2">
            {parseInline(line.replace(/^>\s?/, ''), guildId, key)}
          </div>,
        );
      } else {
        blocks.push(<span key={key}>{parseInline(line, guildId, key)}</span>);
        if (j < lines.length - 1) blocks.push(<br key={`${key}-br`} />);
      }
    });
  });
  return <div className="whitespace-pre-wrap break-words leading-[1.375rem]">{blocks}</div>;
}
